import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

/**
 * MIDI device connection status indicator.
 */
@customElement('midi-status')
export class MidiStatus extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                color: var(--text-secondary);
                padding: 4px 8px;
                background: var(--surface);
                border-radius: 4px;
            }

            :host(:not([visible])) {
                display: none;
            }

            .indicator {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: var(--text-muted);
                flex-shrink: 0;
            }

            .indicator.connected {
                background: var(--success-color);
            }

            .indicator.error {
                background: var(--error-color);
            }
        `,
    ];

    @property({ type: Boolean, reflect: true })
    connected = false;

    @property({ type: String, attribute: 'device-name' })
    deviceName = '';

    @property({ type: String })
    error = '';

    @property({ type: Boolean, reflect: true })
    visible = false;

    private get _statusText(): string {
        if (this.error) return 'MIDI Error';
        if (this.connected) {
            return this.deviceName
                ? `MIDI: ${this.deviceName.substring(0, 20)}`
                : 'MIDI Connected';
        }
        return 'No MIDI Device';
    }

    render() {
        const indicatorClass = this.connected
            ? 'connected'
            : this.error
              ? 'error'
              : '';

        return html`
            <span
                class="indicator ${indicatorClass}"
                title=${this.error || this.deviceName || 'MIDI status'}
            ></span>
            <span>${this._statusText}</span>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'midi-status': MidiStatus;
    }
}
